import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import SystemIcon from '../ShortStatus/SystemIcon';
import Button from '../../../components/CustomButtons/ButtonTS';
import CancelIcon from '@material-ui/icons/Cancel';
import Card from '../../../components/Card/CardTS';
import CardBody from '../../../components/Card/CardBody';
import CardHeader from '../../../components/Card/CardHeaderTS';
import CardIcon from '../../../components/Card/CardIcon';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { closeSweetAlertStyle } from '../../helpers/CustomStyles';
import utils from '../../helpers/GlobalVariables';
import { deleteSystemGroup, closeDeleteSystemGroupNotification } from '../../stores/actions/installerActions';
import { getVisibleSystemGroups } from '../../stores/actions/dashboardActions';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Loading from '../Loading/Loading';

interface RemoveSystemGroupConfirmationProps {
	systemGroup: any;
	deleteSystemGroupSuccessfully: boolean;
	deleteSystemGroupFailed: boolean;
	mobileView: boolean;
	hideRemoveSystemGroupConfirmation: () => void;
	deleteSystemGroup: (systemgroupId: number) => void;
	closeDeleteSystemGroupNotification: () => void;
	getVisibleSystemGroups: () => void;
}

const closeButtonStyle = {
	border: '1px solid #00acc1',
};

const RemoveSystemGroupConfirmationCard = ({
	systemGroup,
	deleteSystemGroupSuccessfully,
	deleteSystemGroupFailed,
	mobileView,
	hideRemoveSystemGroupConfirmation,
	deleteSystemGroup,
	closeDeleteSystemGroupNotification,
	getVisibleSystemGroups,
}: RemoveSystemGroupConfirmationProps) => {
	const [popupTimeoutEnabled, setPopupTimeoutEnabled] = useState(false);
	const [isLoaderRender, setIsLoaderRender] = useState(false);
	useEffect(() => {
		closeDeleteSystemGroupNotification();
	}, [closeDeleteSystemGroupNotification]);

	useEffect(() => {
		if (deleteSystemGroupSuccessfully) {
			setPopupTimeoutEnabled(true);
		}
		if (deleteSystemGroupSuccessfully || deleteSystemGroupFailed) {
			setIsLoaderRender(false);
			const timeoutNotification = setTimeout(() => {
				closeDeleteSystemGroupNotification();
			}, 6000);
			return () => {
				clearTimeout(timeoutNotification);
			};
		}
	}, [closeDeleteSystemGroupNotification, deleteSystemGroupSuccessfully, deleteSystemGroupFailed]);

	useEffect(() => {
		if (popupTimeoutEnabled) {
			const closeWindowTimeout = setTimeout(() => {
				setPopupTimeoutEnabled(false);
				getVisibleSystemGroups();
				hideRemoveSystemGroupConfirmation();
			}, 100);
			return () => {
				clearTimeout(closeWindowTimeout);
			};
		}
	}, [getVisibleSystemGroups, popupTimeoutEnabled, hideRemoveSystemGroupConfirmation]);

	const closeButtonHover = (e) => {
		const button = e.target;
		button.style.backgroundColor = 'rgba(0, 172, 193, 0.1)';
	};

	const closeButtonLeave = (e) => {
		const button = e.target;
		button.style.backgroundColor = '#ffffff';
	};

	const handleDeleteSystemGroup = () => {
		deleteSystemGroup(systemGroup.ID);
	};

	const handleCloseNotification = () => {
		setIsLoaderRender(false);
		closeDeleteSystemGroupNotification();
	};

	const renderDesktopNotifications = (): JSX.Element => {
		if (mobileView) return null;

		const color = deleteSystemGroupSuccessfully ? 'success' : 'danger';
		const message = deleteSystemGroupSuccessfully
			? 'Group was deleted successfully.'
			: 'You do not have the permissions to remove this group.';
		const isOpen = deleteSystemGroupSuccessfully || deleteSystemGroupFailed ? true : false;
		return (
			<Snackbar
				close={true}
				closeNotification={handleCloseNotification}
				color={color}
				message={message}
				open={isOpen}
				place='tc'
			/>
		);
	};

	const renderMobileNotifications = () => {
		const headerStyle = utils.getHeaderStyle(deleteSystemGroupFailed);
		if (!mobileView) return null;
		if (deleteSystemGroupFailed || deleteSystemGroupSuccessfully) {
			return (
				<GridItem xs={12}>
					<h5
						className={deleteSystemGroupFailed ? 'notification-open-create-systemgroup-fail' : 'notification-open-create-systemgroup-success'}
						style={headerStyle}
					>
						{deleteSystemGroupFailed
							? 'You do not have the permissions to remove this group.'
							: 'System Group deleted successfully.'}
					</h5>
				</GridItem>
			);
		}
	};

	const handleRemoveButtonClicked = () => {
		setIsLoaderRender(true);
		handleDeleteSystemGroup();
	};

	return (
		<div>
			<GridContainer>
				<GridItem xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Button
						data-testid='cancelButton'
						simple
						justIcon
						color='info'
						style={closeSweetAlertStyle}
						onClick={hideRemoveSystemGroupConfirmation}
					>
						<CancelIcon style={{ width: '20px', height: '20px' }} />
					</Button>
				</GridItem>
			</GridContainer>
			<Card chart style={{ overflow: 'visible', marginTop: '10px', marginBottom: '0' }} className='node-edit-card'>
				<CardHeader color='info' icon>
					<CardIcon color='info'>
						<SystemIcon />
					</CardIcon>
					<h3 style={utils.sweetAlertHeaderStyle} className='remove-systemgroup-header'>
						{`${systemGroup.name}`}
					</h3>
				</CardHeader>
				<CardBody>
					<GridContainer>
						<GridItem xs={12}>
							<h5>This operation is irreversible.<br />Are you sure you want to delete this System Group?</h5>
						</GridItem>
						{renderMobileNotifications()}
						<GridItem xs={12} style={{ margin: '15px 0 0 0 ' }}>
							<div style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
								<Button
									className='deleteSystemGroupCloseButton'
									simple
									color='info'
									style={closeButtonStyle}
									onClick={hideRemoveSystemGroupConfirmation}
									onMouseEnter={(e) => closeButtonHover(e)}
									onMouseLeave={(e) => closeButtonLeave(e)}
								>
									cancel
								</Button>
								<Button
									style={{ backgroundColor: isLoaderRender ? '#ccc' : '#f44336' }}
									onClick={handleRemoveButtonClicked}
									disabled={isLoaderRender}
								>
									{isLoaderRender ? <Loading transform={1} height={17} width={48} color='darkGray' /> : 'remove'}
								</Button>
							</div>
						</GridItem>
					</GridContainer>
				</CardBody>
			</Card>
			{renderDesktopNotifications()}
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		systemgroups: state.dashboard.systemgroups,
		deleteSystemGroupSuccessfully: state.installer.deleteSystemGroupSuccessfully,
		deleteSystemGroupFailed: state.installer.deleteSystemGroupFailed,
		mobileView: state.dashboard.mobileView,
	};
};

const actions = {
	deleteSystemGroup,
	closeDeleteSystemGroupNotification,
	getVisibleSystemGroups,
};

export default connect(mapStateToProps, actions)(RemoveSystemGroupConfirmationCard);
